
// Haptic feedback utilities for vibration effects
import { isMobile } from '@/utils/device';

// Haptic feedback patterns
export type HapticPattern = 
  | 'success' 
  | 'error' 
  | 'warning' 
  | 'light' 
  | 'medium' 
  | 'heavy' 
  | 'selection' 
  | 'impact';

// Define vibration patterns (in milliseconds)
// Format: [vibrate, pause, vibrate, pause, ...]
export const HAPTIC_PATTERNS = {
  success: [50, 30, 100, 30, 50],
  error: [100, 30, 100, 30, 100, 30, 100],
  warning: [100, 50, 100],
  light: [10],
  medium: [35],
  heavy: [65],
  selection: [20],
  impact: [80, 5, 20],
};

let isHapticPrimed = false;
let isHapticAvailable = false;

// Test if haptic feedback is actually available
// Some browsers report they support navigator.vibrate but don't actually vibrate
const testHapticAvailability = (): boolean => {
  if (!navigator.vibrate) return false;
  
  try {
    // Try a minimal vibration to test
    navigator.vibrate(1);
    return true;
  } catch (e) {
    console.error("Haptic test failed:", e);
    return false;
  }
};

// Prime the haptic feedback system with a minimal vibration
// This helps on iOS where the first vibration after page load often doesn't work
export const primeHapticFeedback = (): boolean => {
  if (isHapticPrimed) return isHapticAvailable;
  
  // Check if device is mobile first
  if (!isMobile()) {
    console.log("Haptics not supported on non-mobile devices");
    return false;
  }
  
  // Test if vibration is available
  isHapticAvailable = testHapticAvailability();
  
  if (isHapticAvailable) {
    try {
      // Very minimal vibration that user won't notice
      navigator.vibrate(1);
      isHapticPrimed = true;
      console.log("Haptic feedback system primed");
    } catch (e) {
      console.error("Could not prime haptics:", e);
      isHapticAvailable = false;
    }
  } else {
    console.log("Vibration API not supported on this device");
  }
  
  return isHapticAvailable;
};

// Check if haptics are currently available
export const isHapticFeedbackAvailable = (): boolean => {
  if (!isHapticPrimed) {
    // Try to prime if not already done
    return primeHapticFeedback();
  }
  return isHapticAvailable;
};

// Enhanced function to trigger haptic feedback with specific pattern
export const triggerHapticFeedback = (pattern: HapticPattern = 'medium'): boolean => {
  // Prime if not already done
  if (!isHapticPrimed) {
    primeHapticFeedback();
  }
  
  if (!isHapticAvailable) {
    return false;
  }
  
  try {
    // Get the pattern array
    const vibrationPattern = HAPTIC_PATTERNS[pattern] || HAPTIC_PATTERNS.medium;
    
    // Trigger the vibration with the selected pattern
    navigator.vibrate(vibrationPattern);
    console.log(`Haptic feedback triggered: ${pattern}`);
    return true;
  } catch (e) {
    console.error(`Failed to trigger haptic feedback (${pattern}):`, e);
    
    // Try a simple fallback pattern
    try {
      navigator.vibrate(50);
      return true;
    } catch (e2) {
      console.error("Fallback haptic feedback also failed:", e2);
      return false;
    }
  }
};

/**
 * Trigger a heart-beat-rhythm haptic at the given bpm.
 * Plays a 3-beat "lub-dub" pattern; pulse length scales with bpm so higher
 * bpm feels heavier. No-op on non-mobile / unsupported devices.
 */
export const triggerBpmPulse = (bpm: number): boolean => {
  if (!isHapticFeedbackAvailable()) return false;
  const safeBpm = Math.max(40, Math.min(220, Math.round(bpm)));
  const beatGap = Math.round(60000 / safeBpm);
  const lubDubGap = Math.max(60, Math.min(140, Math.round(beatGap * 0.18)));
  const pulseLen = Math.max(40, Math.min(90, Math.round(40 + safeBpm * 0.15)));
  const dub = Math.round(pulseLen * 0.6);
  const beatLen = pulseLen + lubDubGap + dub;
  const rest = Math.max(40, beatGap - beatLen);
  const pattern = [
    pulseLen, lubDubGap, dub, rest,
    pulseLen, lubDubGap, dub, rest,
    pulseLen, lubDubGap, dub,
  ];
  try {
    navigator.vibrate(pattern);
    return true;
  } catch (e) {
    console.error("Failed to trigger bpm pulse:", e);
    return false;
  }
};

// For complex custom patterns
export const triggerCustomHapticPattern = (pattern: number[]): boolean => {
  if (!isHapticFeedbackAvailable()) {
    return false;
  }
  
  try {
    navigator.vibrate(pattern);
    console.log("Custom haptic pattern triggered");
    return true;
  } catch (e) {
    console.error("Failed to trigger custom haptic pattern:", e);
    return false;
  }
};

